import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { memberAPI } from '../services/api';
import { useData } from '../contexts/DataContext';
import CollapsibleSection from './CollapsibleSection';
import DetailsSection from './DetailsSection';
import TimelineSection from './TimelineSection';
import RelationsSection from './RelationsSection';
import './MemberForm.css';

const SECTION_STORAGE_KEY = 'memberForm.sections';

function MemberForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { members: allMembers, refreshMembers } = useData();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    place_of_birth: '',
    date_of_death: '',
    place_of_death: '',
    details: {},
    events: [],
    biography: '',
  });
  const [photo, setPhoto] = useState(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [memberData, setMemberData] = useState(null);

  // Section expand state - load from localStorage
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = localStorage.getItem(SECTION_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {
        details: false,
        timeline: false,
        relations: false,
        biography: false,
        photo: false,
      };
    } catch {
      return {
        details: false,
        timeline: false,
        relations: false,
        biography: false,
        photo: false,
      };
    }
  });

  // Save section state to localStorage
  useEffect(() => {
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const fetchMember = useCallback(async () => {
    try {
      const response = await memberAPI.getById(id);
      const member = response.data;
      setMemberData(member);
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        date_of_birth: member.date_of_birth || '',
        place_of_birth: member.place_of_birth || '',
        date_of_death: member.date_of_death || '',
        place_of_death: member.place_of_death || '',
        details: member.details || {},
        events: member.events || [],
        biography: member.biography || '',
      });
      setCurrentPhotoUrl(member.photo_url);
    } catch (err) {
      setError(t('member.fetchFailed'));
    }
  }, [id, t]);

  useEffect(() => {
    if (isEdit) {
      fetchMember();
    }
  }, [isEdit, fetchMember]);

  const handleBasicChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDetailsChange = (details) => {
    setFormData({ ...formData, details });
  };

  const handleBiographyChange = (e) => {
    setFormData({ ...formData, biography: e.target.value });
  };

  const handlePhotoChange = (e) => {
    setPhoto(e.target.files[0]);
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setCurrentPhotoUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name.trim()) {
      setError(t('member.firstNameRequired'));
      return;
    }

    setLoading(true);
    setError('');

    const data = new FormData();
    data.append('first_name', formData.first_name);
    data.append('last_name', formData.last_name);
    data.append('date_of_birth', formData.date_of_birth);
    data.append('place_of_birth', formData.place_of_birth);
    data.append('date_of_death', formData.date_of_death);
    data.append('place_of_death', formData.place_of_death);
    data.append('details', JSON.stringify(formData.details));
    data.append('events', JSON.stringify(formData.events));
    data.append('biography', formData.biography);

    if (photo) {
      data.append('photo', photo);
    }

    try {
      if (isEdit) {
        await memberAPI.update(id, data);
        await refreshMembers();
        navigate(`/member/${id}`);
      } else {
        const response = await memberAPI.create(data);
        await refreshMembers();
        navigate(`/member/${response.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('member.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>{isEdit ? t('member.editTitle') : t('member.addTitle')}</h2>
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic Info - Always visible */}
          <div className="basic-info-section">
            <div className="form-row">
              <div className="form-group">
                <label>{t('member.firstName')} *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleBasicChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('member.lastName')}</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleBasicChange}
                />
              </div>
            </div>
          </div>

          {/* Details Section */}
          <CollapsibleSection
            title={t('member.details')}
            isExpanded={expandedSections.details}
            onToggle={() => toggleSection('details')}
          >
            <DetailsSection
              details={formData.details}
              onChange={handleDetailsChange}
            />
          </CollapsibleSection>

          {/* Timeline Section */}
          <CollapsibleSection
            title={t('member.timeline')}
            isExpanded={expandedSections.timeline}
            onToggle={() => toggleSection('timeline')}
          >
            <TimelineSection
              formData={formData}
              onChange={setFormData}
            />
          </CollapsibleSection>

          {/* Relations Section */}
          {isEdit && memberData && (
            <CollapsibleSection
              title={t('member.relations')}
              isExpanded={expandedSections.relations}
              onToggle={() => toggleSection('relations')}
            >
              <RelationsSection
                member={memberData}
                allMembers={allMembers}
              />
            </CollapsibleSection>
          )}

          {/* Biography Section */}
          <CollapsibleSection
            title={t('member.biography')}
            isExpanded={expandedSections.biography}
            onToggle={() => toggleSection('biography')}
          >
            <textarea
              name="biography"
              value={formData.biography}
              onChange={handleBiographyChange}
              rows="6"
              placeholder={t('member.biographyPlaceholder')}
              className="biography-textarea"
            />
          </CollapsibleSection>

          {/* Photo Section */}
          <CollapsibleSection
            title={t('member.photo')}
            isExpanded={expandedSections.photo}
            onToggle={() => toggleSection('photo')}
          >
            <div className="photo-section">
              {currentPhotoUrl && !photo && (
                <div className="current-photo">
                  <img src={currentPhotoUrl} alt="Current" />
                </div>
              )}
              {photo && (
                <div className="current-photo">
                  <img src={URL.createObjectURL(photo)} alt="New" />
                </div>
              )}
              <div className="photo-actions">
                <label className="photo-upload-btn">
                  {currentPhotoUrl || photo ? t('common.change') : t('common.upload')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    hidden
                  />
                </label>
                {(currentPhotoUrl || photo) && (
                  <button
                    type="button"
                    className="photo-remove-btn"
                    onClick={handleRemovePhoto}
                  >
                    {t('common.remove')}
                  </button>
                )}
              </div>
            </div>
          </CollapsibleSection>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.saving') : t('common.save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MemberForm;
